import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, SideDrawer } from 'vopay';
import type { AccountUserRole } from 'vopay';
import { Button } from 'UI';
import styles from './RoleDetailsDrawer.module.scss';

type RoleDetailsDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  usersCount: number;
  fetchingUsers: boolean;
  subAccountsEnabled: boolean;
  onClose: () => void;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onViewPermissions: () => void;
  onViewUsers: () => void;
};

const RoleDetailsDrawer: FC<RoleDetailsDrawerProps> = ({
  isOpen,
  role,
  usersCount,
  fetchingUsers,
  subAccountsEnabled,
  onClose,
  canManage,
  onEdit,
  onDelete,
  onViewPermissions,
  onViewUsers,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserRoles.detailsDrawer' });

  if (!role) return null;

  const permissionsCount = role.Permissions.length;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Role Details' })}
      footer={
        canManage ? (
          <Box className={styles.footer}>
            <Button colorScheme="secondary" onClick={onDelete}>
              {t('deleteRole', { defaultValue: 'Delete Role' })}
            </Button>
            <Button colorScheme="primary" onClick={onEdit}>
              {t('editRole', { defaultValue: 'Edit Role' })}
            </Button>
          </Box>
        ) : undefined
      }
    >
      <div className={styles.body}>
        <div className={styles.detailsCard}>
          <div className={styles.row}>
            <span className={styles.label}>{t('roleName', { defaultValue: 'Role name' })}</span>
            <span className={styles.value}>{role.Name}</span>
          </div>

          <div className={styles.row}>
            <span className={styles.label}>
              {t('permissionsEnabled', { defaultValue: 'Permissions enabled' })}
            </span>
            <button type="button" className={styles.valueClickable} onClick={onViewPermissions}>
              {permissionsCount}
            </button>
          </div>

          {subAccountsEnabled && (
            <div className={styles.row}>
              <span className={styles.label}>
                {t('appliesToSubaccounts', { defaultValue: 'Applies to sub-accounts' })}
              </span>
              <span className={styles.value}>
                {role.ApplyToAllSubaccounts
                  ? t('yes', { defaultValue: 'Yes' })
                  : t('no', { defaultValue: 'No' })}
              </span>
            </div>
          )}

          <div className={styles.row}>
            <span className={styles.label}>{t('users', { defaultValue: 'Users' })}</span>
            <button
              type="button"
              className={styles.valueClickable}
              disabled={fetchingUsers || usersCount === 0}
              onClick={usersCount > 0 ? onViewUsers : undefined}
            >
              {fetchingUsers || usersCount === 0 ? '—' : usersCount}
            </button>
          </div>
        </div>
      </div>
    </SideDrawer>
  );
};

export default RoleDetailsDrawer;
